import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { CloudOff, Lock, SearchX } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { TryAnotherAccount } from "@/features/auth/try-another-account";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Entity label (e.g. "inspection", "template") to customize the copy. */
  entityLabel?: string;
  /** Custom title and description (override the default copy). */
  title?: string;
  description?: string;
  /** Primary action (e.g. back home / to the parent listing). */
  primaryAction?: { label: string; onClick: () => void };
  /** Optional secondary action (e.g. retry). */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
}

const ICONS: Record<NotFoundVariant, Component<{ class?: string }>> = {
  "not-found": SearchX,
  "no-access": Lock,
  offline: CloudOff,
};

/**
 * "Not found / no access / offline" state for detail routes
 * whose entity could not be resolved (see `createEntityResolution`). It reuses the
 * premium `EmptyState` (brand-aware illustration, animations under a
 * reduced-motion guard) and offers default localized copy + clear actions, instead
 * of leaving the screen loading indefinitely.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";

  const defaultTitle = (): string => {
    switch (variant()) {
      case "no-access": {
        return t(msg`You don't have access`);
      }
      case "offline": {
        return t(msg`Can't load this yet`);
      }
      default: {
        return t(msg`Not found`);
      }
    }
  };

  const defaultDescription = (): string => {
    switch (variant()) {
      case "no-access": {
        return t(msg`This item doesn't exist or you don't have permission to view it.`);
      }
      case "offline": {
        return t(
          msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
        );
      }
      default: {
        return t(msg`This item may have been deleted, or the link is no longer valid.`);
      }
    }
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-2 sm:flex-row">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
      <TryAnotherAccount />
    </div>
  );

  // The icon becomes interactive and fires the primary action (or the
  // secondary one if there is no primary), so that tapping it is not a dead
  // end (kit #5).
  const iconAction = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      icon={ICONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={(props.primaryAction ?? props.secondaryAction) ? action() : undefined}
      onIconActivate={iconAction() ? () => iconAction()?.onClick() : undefined}
      iconActionLabel={iconAction()?.label}
      class={props.class}
    />
  );
};
